import { HeldInputLedger } from '@playcastdotio/PeerCore';
import type { ITrialInputBinding, TuningIdentity } from '@playcastdotio/PeerCore';
import { generators } from '@playcastdotio/Messaging';
import type { PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../../services/ipc/IIpcService';

export type HostTrialInputBindingOptions = {
  ipc: Pick<IIpcService, 'send'>; hostId: () => string;
  canNeutralizeGamepad: (peerId: string) => boolean;
  neutralizeGamepad: (peerId: string) => Promise<number>;
};

/** Releases only retained controls this host actually accepted, even after the guest channel is gone. */
export class HostTrialInputBinding implements ITrialInputBinding {
  private readonly ledgers = new Map<string, HeldInputLedger>();
  private readonly suspended = new Set<string>();
  constructor(private readonly options: HostTrialInputBindingOptions) {}
  registerPeer(peerId: string): void { if (!this.ledgers.has(peerId)) this.ledgers.set(peerId, new HeldInputLedger()); }
  allows(peerId: string): boolean { return !this.suspended.has(peerId); }
  observeForwarded(peerId: string, message: PlaycastMessage<PlaycastMessageTarget>): void {
    this.registerPeer(peerId); this.ledgers.get(peerId)?.observe(message);
  }
  suspend(target: TuningIdentity): void { this.registerPeer(target.peerId); this.suspended.add(target.peerId); }
  async neutralize(target: TuningIdentity): Promise<{ releasesForwarded: number; delivery: 'forwarded' | 'unavailable' }> {
    const ledger = this.ledgers.get(target.peerId);
    if (!ledger?.safe()) return { releasesForwarded: 0, delivery: 'unavailable' };
    const releases = ledger.releases();
    const others = [...this.ledgers].filter(([id]) => id !== target.peerId).map(([, value]) => value);
    if (releases.some(release => (release.kind === 'gamepad' && !this.options.canNeutralizeGamepad(target.peerId))
      || ((release.kind === 'touch' || release.kind === 'legacy-touch') && others.some(other => other.has(release.id))))) {
      return { releasesForwarded: 0, delivery: 'unavailable' };
    }
    let forwarded = 0;
    for (const release of releases) {
      if ((release.kind === 'keyboard' || release.kind === 'mouse') && others.some(other => other.has(release.id))) continue;
      if (release.kind === 'gamepad') { forwarded += await this.options.neutralizeGamepad(target.peerId); continue; }
      const hostId = this.options.hostId();
      const message = release.kind === 'keyboard' ? generators.keyboard.up(hostId, release.detail)
        : release.kind === 'mouse' ? generators.mouse.up(hostId, release.detail)
          : release.kind === 'touch' ? generators.touch.cancel(hostId, { changedTouches: [release.detail] })
            : generators.touch.up(hostId, release.detail);
      this.options.ipc.send(message); forwarded++;
    }
    ledger.clear(); return { releasesForwarded: forwarded, delivery: 'forwarded' };
  }
  resume(target: TuningIdentity): void { this.suspended.delete(target.peerId); }
  dispose(target: TuningIdentity): void { this.ledgers.delete(target.peerId); this.suspended.delete(target.peerId); }
}
